import React, { useContext } from 'react'

export type AnalyticsAttributeTypes =
  | string
  | number
  | boolean
  | object
  | undefined

export interface AnalyticsEvent {
  name: string
  extras?: { [key: string]: AnalyticsAttributeTypes }
}

export interface AnalyticsTracker {
  //TODO: drop this method once all analytics are migrated to events
  track(
    event: string,
    extras?: { [key: string]: AnalyticsAttributeTypes }
  ): void
  trackEvent(event: AnalyticsEvent): void
  updateProperties(key: string, value: string): Promise<void>
}

export abstract class AnalyticsTrackerBase {
  abstract track(
    event: string,
    extras?: { [key: string]: AnalyticsAttributeTypes }
  ): void
  trackEvent(event: AnalyticsEvent): void {
    this.track(event.name, event.extras)
  }
  abstract updateProperties(key: string, value: string): Promise<void>
}

export class NullAnalyticsTracker extends AnalyticsTrackerBase {
  track(
    event: string,
    extras?: { [key: string]: AnalyticsAttributeTypes }
  ): void {
    console.debug(
      `Null tracker: event=${event} properties=${JSON.stringify(extras)}`
    )
  }
  async updateProperties(key: string, value: string) {}
}

class AnalyticsWrapper implements AnalyticsTracker {
  trackers: [AnalyticsTracker] = [new NullAnalyticsTracker()]

  track(event: string, extras?: { [key: string]: AnalyticsAttributeTypes }) {
    this.trackers.forEach((tracker) => tracker.track(event, extras))
  }

  trackEvent(event: AnalyticsEvent): void {
    this.trackers.forEach((tracker) => tracker.trackEvent(event))
  }

  async updateProperties(key: string, value: string) {
    await Promise.all(
      this.trackers.map(
        async (tracker) => await tracker.updateProperties(key, value)
      )
    )
  }

  setTrackers(newTrackers: [AnalyticsTrackerBase]) {
    this.trackers = newTrackers
  }

  disable() {
    this.trackers = [new NullAnalyticsTracker()]
  }
}

export const analytics = new AnalyticsWrapper()

export const Analytics = React.createContext<AnalyticsTracker>(analytics)
export const useAnalytics = () => useContext(Analytics)
